/* eslint-disable no-underscore-dangle */
import castArray from 'lodash/castArray.js';
import reject from 'lodash/reject.js';
import has from 'lodash/has.js';
import get from 'lodash/get.js';
import findIndex from 'lodash/findIndex.js';
import { snakeCase } from 'change-case';

class QueryBuilder {
    constructor(endpoint) {
        this.baseUrl = '/api';

        this.setEndpoint(endpoint);

        this._includes = [];
        this._filters = [];
        this._fields = [];
        this._appends = [];
        this._sorts = [];
        this._custom_filters = [];
        this._pagination = null;

        this._raw = {};
        this._arrayRaw = {};
    }

    withoutBase() {
        this.baseUrl = '';

        return this;
    }

    withMenu() {
        this.raw({ menu: 'true' });

        return this;
    }

    setEndpoint(endpoint) {
        this.endpoint = endpoint;

        return this;
    }

    addItems(type, items) {
        castArray(items).forEach(item => this[`_${type}`].push(item));
    }

    include(includes) {
        this.addItems('includes', includes);

        return this;
    }

    applyToModel(model, pagination) {
        const query = model.query();

        if (this._includes.length) {
            const relationships = this._includes.map(nestedRelationship =>
                nestedRelationship
                    .split('.')
                    .map(relationship => snakeCase(relationship))
                    .join('.'),
            );

            query.with(relationships);
        }

        if (this._sorts) {
            this._sorts.forEach(sort => {
                if (sort[0] === '-') {
                    query.orderBy(sort.substr(1), 'desc');
                } else {
                    query.orderBy(sort, 'asc');
                }
            });
        }

        if (this.hasTagSearch()) {
            query.whereHas('tags', tagQuery => {
                tagQuery.where(tag => tag.name.en === this._raw.tags);
            });
        }

        if (this.hasCustomFilters()) {
            this._custom_filters.forEach(({ filter, value, field }) => {
                filter.applyToModel(query, value, field);
            });
        }

        if (this.hasGroupSearch()) {
            query.whereHas('groups', groupQuery => {
                groupQuery.where(group => group.name === this._raw.groups);
            });
        }

        if (this.hasSearch()) {
            if (!Object.prototype.hasOwnProperty.call(model, 'searchable')) {
                throw new Error('Model must implement a searchable() method specifying which keys are searchable.');
            }

            if (['course_enrollments', 'attempt_authorizations'].includes(model.entity)) {
                query.whereHas('user', userQuery => {
                    userQuery.search(this._raw.search, { keys: model.searchable() });
                });
            } else {
                query.search(this._raw.search, { keys: model.searchable() });
            }
        }

        this._filters.forEach(filter => {
            if (get(filter, 'options.ignoreFromModel')) {
                return;
            }

            if (get(filter, 'options.custom')) {
                query.where(filter.options.custom);
                return;
            }

            query.where(filter.key, filter.value);
        });

        if (pagination?.entity_ids) {
            query.whereIdIn(pagination.entity_ids);
        }

        return query;
    }

    getFilter(key) {
        return this._filters.find(filter => filter.key === key);
    }

    removeFilter(key) {
        this._filters = reject(this._filters, filter => filter.key === key);

        return this;
    }

    hasFilter(key) {
        return !!this._filters.find(filter => filter.key === key);
    }

    customFilter(filter) {
        this._custom_filters.push(filter);
    }

    filter(key, value, options) {
        if (this.hasFilter(key)) {
            this.removeFilter(key);
        }

        this.addItems('filters', { key, value, options });

        return this;
    }

    filters(filters) {
        this.addItems('filters', filters);

        return this;
    }

    fields(fields) {
        this.addItems('fields', fields);

        return this;
    }

    append(appends) {
        this.addItems('appends', appends);

        return this;
    }

    sort(sorts, direction) {
        if (!direction || direction === 'asc') {
            this.addItems('sorts', sorts);
        } else {
            this.sortDesc(sorts);
        }

        return this;
    }

    sortDesc(sorts) {
        this.addItems(
            'sorts',
            castArray(sorts).map(sort => `-${sort}`),
        );

        return this;
    }

    clearSorts() {
        this._sorts = [];

        return this;
    }

    search(query) {
        if (!query) {
            return this.removeSearch();
        }

        this.raw({ search: query });

        return this;
    }

    removeSearch() {
        this.removeRaw('search');

        return this;
    }

    removeCustomFilter(field) {
        const index = findIndex(this._custom_filters, customFilter => customFilter.field === field);

        if (index !== -1) {
            this._custom_filters.splice(index, 1);
        }
    }

    menu() {
        this.raw({ menu: 'true' });
    }

    array(field, value) {
        this._arrayRaw[field] = value;

        return this;
    }

    raw(params) {
        this._raw = {
            ...this._raw,
            ...params,
        };

        return this;
    }

    hasRaw(key) {
        return has(this._raw, key);
    }

    removeRaw(key) {
        delete this._raw[key];
    }

    removeArray(key) {
        delete this._arrayRaw[key];
    }

    page(page) {
        return this.setPagination({ page });
    }

    pageSize(size) {
        return this.raw({ per_page: size });
    }

    setPagination(pagination) {
        this._pagination = pagination;

        return this;
    }

    when(truth, handler) {
        if (truth) {
            handler(this);
        }

        return this;
    }

    hasSearch() {
        return Object.prototype.hasOwnProperty.call(this._raw, 'search');
    }

    hasTagSearch() {
        return Object.prototype.hasOwnProperty.call(this._raw, 'tags');
    }

    hasGroupSearch() {
        return Object.prototype.hasOwnProperty.call(this._raw, 'groups');
    }

    hasCustomFilters() {
        return this._custom_filters.length > 0;
    }

    getCustomFilter(field) {
        return this._custom_filters.find(customFilter => customFilter.field === field);
    }

    hasItems() {
        return (
            this._includes.length +
                this._filters.length +
                this._appends.length +
                this._sorts.length +
                this._fields.length +
                Object.keys(this._raw).length >
            0
        );
    }

    toUrl() {
        const url = `${this.baseUrl}/${this.endpoint}`;

        const params = {};

        if (this._includes.length) {
            params.include = this._includes.join(',');
        }

        if (this._filters.length) {
            this._filters.forEach(({ key, value }) => {
                params[`filter[${key}]`] = value;
            });
        }

        if (this._custom_filters.length) {
            this._custom_filters.forEach(({ filter, field, value }) => {
                if (filter.applyToParams) {
                    filter.applyToParams(params, value, field);
                } else {
                    params[field] = value;
                }
            });
        }

        if (this._fields.length) {
            this._fields.forEach(({ key, value }) => {
                params[`fields[${key}]`] = value;
            });
        }

        if (this._appends.length) {
            params.append = this._appends.join(',');
        }

        if (this._sorts.length) {
            params.sort = this._sorts.join(',');
        }

        if (Object.keys(this._raw).length) {
            Object.keys(this._raw).forEach(key => {
                params[key] = this._raw[key];
            });
        }

        if (this._pagination) {
            params.page = this._pagination.page;
        }

        let query = Object.keys(params)
            .map(key => `${key}=${params[key]}`)
            .join('&');

        if (Object.keys(this._arrayRaw).length) {
            Object.keys(this._arrayRaw).forEach((key, arrayKeyIndex) => {
                this._arrayRaw[key].forEach((value, index) => {
                    const concatenator = Object.keys(params).length > 0 || index > 0 || arrayKeyIndex > 0 ? '&' : '?';

                    if (index === 0 && arrayKeyIndex === 0 && _.isEmpty(params)) {
                        query += `${key}[]=${value}`;
                    } else {
                        query += `${concatenator}${key}[]=${value}`;
                    }
                });
            });
        }

        return `${url}?${query}`;
    }
}

export default QueryBuilder;
